import { useMemo, useState, type FormEvent } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import { AfosLogo } from '../components/Logo'
import { OrbitField } from '../components/OrbitField'

export function InvitePage() {
  const [params] = useSearchParams()
  const inviteId = params.get('id') || ''
  const token = params.get('token') || ''
  const [otp, setOtp] = useState('')
  const [name, setName] = useState('')
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const ready = useMemo(() => Boolean(inviteId && token), [inviteId, token])

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setMessage(null)
    try {
      const res = await fetch('/api/afos360/admin/invites/accept', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          invite_id: inviteId,
          token,
          otp,
          name: name || undefined,
        }),
      })
      if (!res.ok) throw new Error(await res.text())
      const user = await res.json()
      setMessage(`Welcome ${user.email}. 2FA enabled via invite OTP. Continue to sign in.`)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invite accept failed')
    }
  }

  return (
    <div className="afos-shell relative flex min-h-screen flex-col">
      <OrbitField />
      <header className="relative z-10 px-4 py-6 sm:px-8">
        <Link to="/" className="inline-flex">
          <AfosLogo size={32} />
        </Link>
      </header>
      <main className="relative z-10 flex flex-1 items-center justify-center px-4 pb-16 sm:px-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass neon-ring w-full max-w-md rounded-[2rem] p-6 sm:p-8"
      >
        <h1 className="text-center font-[family-name:var(--font-display)] text-3xl font-extrabold">
          Secure invite gate
        </h1>
        <p className="mt-2 text-center text-sm text-[var(--color-mist)]">
          Enter the SendGrid OTP from your invite email to activate your AFOS 360 agent access.
        </p>
        {!ready && (
          <p className="mt-4 text-center text-sm text-[var(--color-rose)]">
            Missing invite id/token in the URL.
          </p>
        )}
        <form className="mt-6 space-y-3" onSubmit={onSubmit}>
          <input
            className="w-full rounded-2xl border border-[var(--color-line)] bg-black/30 px-4 py-3"
            placeholder="Your name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <input
            className="w-full rounded-2xl border border-[var(--color-line)] bg-black/30 px-4 py-3 tracking-[0.35em]"
            placeholder="6-digit OTP"
            value={otp}
            onChange={(e) => setOtp(e.target.value)}
            inputMode="numeric"
            required
          />
          <button
            type="submit"
            disabled={!ready}
            className="w-full rounded-full bg-[var(--color-cyan)] px-4 py-3 font-semibold text-[var(--color-void)] disabled:opacity-40"
          >
            Verify OTP & activate
          </button>
        </form>
        {message && <p className="mt-4 text-center text-sm text-[var(--color-cyan)]">{message}</p>}
        {error && <p className="mt-4 text-center text-sm text-[var(--color-rose)]">{error}</p>}
        <div className="mt-6 text-center text-sm">
          <Link to="/signin" className="text-[var(--color-amber)]">
            Back to sign in
          </Link>
          {' · '}
          <Link to="/signup" className="text-[var(--color-cyan)]">
            Start free trial
          </Link>
        </div>
      </motion.div>
      </main>
    </div>
  )
}
